
import { useRef, useState, useEffect } from 'react';

export const useCardHoverEffect = (featured: boolean = false) => {
  const [isHovered, setIsHovered] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  
  // Handle hover effects for featured card with smoother transitions
  useEffect(() => {
    if (!featured || !cardRef.current) return;
    
    // Cache the bounding rect to avoid forced reflows on mousemove
    let cachedRect: DOMRect | null = null;
    
    const updateCachedRect = () => {
      if (cardRef.current) {
        cachedRect = cardRef.current.getBoundingClientRect();
      }
    };
    
    // Initialize cached rect
    updateCachedRect();
    
    // Use ResizeObserver to update cached rect when element size changes
    const resizeObserver = new ResizeObserver(updateCachedRect);
    resizeObserver.observe(cardRef.current);
    
    const handleMouseMove = (e: MouseEvent) => {
      if (!cardRef.current || !cachedRect) return;
      
      // Use cached rect instead of calling getBoundingClientRect()
      const x = e.clientX - cachedRect.left;
      const y = e.clientY - cachedRect.top;
      
      const centerX = cachedRect.width / 2;
      const centerY = cachedRect.height / 2;
      
      // Reduced rotation sensitivity for subtler effect
      const rotateX = (y - centerY) / 40;
      const rotateY = (centerX - x) / 40;
      
      cardRef.current.style.transform = `
        perspective(1000px)
        rotateX(${rotateX}deg)
        rotateY(${rotateY}deg)
        scale3d(1.01, 1.01, 1.01)
      `;
    };
    
    const handleMouseLeave = () => {
      if (!cardRef.current) return;
      // Adding easing for smoother transition back to default state
      cardRef.current.style.transition = 'transform 0.6s cubic-bezier(0.23, 1, 0.32, 1)';
      cardRef.current.style.transform = 'perspective(1000px) rotateX(0) rotateY(0) scale3d(1, 1, 1)';
    };
    
    const handleMouseEnter = () => {
      if (!cardRef.current) return;
      // Adding easing for smoother transition to hover state
      cardRef.current.style.transition = 'transform 0.3s cubic-bezier(0.23, 1, 0.32, 1)';
    };
    
    const card = cardRef.current;
    
    if (isHovered) {
      card.addEventListener('mousemove', handleMouseMove);
      card.addEventListener('mouseleave', handleMouseLeave);
      card.addEventListener('mouseenter', handleMouseEnter);
    }
    
    return () => {
      card.removeEventListener('mousemove', handleMouseMove);
      card.removeEventListener('mouseleave', handleMouseLeave);
      card.removeEventListener('mouseenter', handleMouseEnter);
      resizeObserver.disconnect();
    };
  }, [isHovered, featured]);
  
  return {
    cardRef,
    isHovered,
    setIsHovered
  };
};
